import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, ChartStyle, PlanetPosition, KundaliChartData, PlanetName } from '../types/kundali';
import { NorthIndianSvgChart, SouthIndianSvgChart, EastIndianSvgChart } from './ChartViewer';
import { ResponsiveChartContainer } from './ResponsiveChartContainer';
import { formatDOB } from '../utils/dateUtils';
import { calculateAllPlanetaryTransitPeriods, PlanetaryTransitPeriod } from '../utils/gocharPeriodEngine';
import { Calendar, Clock, Sparkles } from 'lucide-react';

export type GocharChartLayout = 'grid' | 'stacked' | 'natal' | 'transit';

interface GocharDualChartProps {
  kundali: CompleteKundali;
  transitPlanets: PlanetPosition[];
  transitDateFormatted: string;
  transitDateObj?: Date;
  chartStyle: ChartStyle;
  layoutMode?: GocharChartLayout;
  chartScale?: number; // Zoom level / user density preference
  lang?: Language;
  showDegrees?: boolean;
  selectedTransitPlanet?: PlanetName | null;
  onSelectTransitPlanet?: (planet: PlanetName) => void;
  highlightedHouses?: number[];
  highlightedAspectHouses?: number[];
  affectedNatalPlanets?: string[];
}

export const GocharDualChart: React.FC<GocharDualChartProps> = ({
  kundali,
  transitPlanets,
  transitDateFormatted,
  transitDateObj = new Date(),
  chartStyle,
  layoutMode = 'grid',
  chartScale = 100,
  lang = 'en',
  showDegrees = true,
  selectedTransitPlanet,
  onSelectTransitPlanet,
  highlightedHouses = [],
  highlightedAspectHouses = [],
  affectedNatalPlanets = []
}) => {
  const isHi = lang === 'hi';
  const natalAsc = kundali.planets.find(p => p.name === 'Ascendant') || { signIndex: 0 };
  const ascSignIdx = natalAsc.signIndex;

  const [localPeriodPlanet, setLocalPeriodPlanet] = useState<PlanetName>('Saturn');
  const activeFocusPlanet = selectedTransitPlanet || localPeriodPlanet;

  // Compute live transit periods (start and end dates) for all planets for the selected date
  const transitPeriods: Record<PlanetName, PlanetaryTransitPeriod> = useMemo(() => {
    return calculateAllPlanetaryTransitPeriods(transitDateObj);
  }, [transitDateObj]);

  const activePeriod = transitPeriods[activeFocusPlanet] || transitPeriods['Saturn'];

  // Create valid KundaliChartData for transit planets positioned relative to Natal Lagna
  const transitChartData: KundaliChartData = {
    divisionalCode: 'Transit',
    title: isHi ? 'गोचर चक्र (लग्न सापेक्ष)' : 'Gochar (Natal Ascendant Relative)',
    ascendantIndex: ascSignIdx,
    houses: kundali.charts.D1.houses,
    planets: transitPlanets
  };

  const renderSvg = (isTransit: boolean) => {
    const data = isTransit ? transitChartData : kundali.charts.D1;
    const pList = isTransit ? transitPlanets : kundali.planets;

    if (chartStyle === 'south') {
      return (
        <SouthIndianSvgChart
          chartData={data}
          planets={pList}
          lang={lang}
          showDegrees={showDegrees}
        />
      );
    }
    if (chartStyle === 'east') {
      return (
        <EastIndianSvgChart
          chartData={data}
          planets={pList}
          lang={lang}
          showDegrees={showDegrees}
        />
      );
    }
    return (
      <NorthIndianSvgChart
        chartData={data}
        planets={pList}
        lang={lang}
        showDegrees={showDegrees}
      />
    );
  };

  // Determine max dimension based on layout mode and scale multiplier
  const baseMaxDim = (layoutMode === 'stacked' || layoutMode === 'natal' || layoutMode === 'transit') ? 850 : 650;
  const scaledMaxDim = Math.round(baseMaxDim * (chartScale / 100));

  // Cross-Highlight Indicator Bar
  const crossHighlightBar = selectedTransitPlanet ? (
    <div className="w-full bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex flex-wrap items-center justify-between gap-2.5 text-xs text-astro-text mb-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="flex items-center gap-1.5 font-bold text-astro-primary bg-amber-500/20 px-2.5 py-1 rounded-lg border border-amber-500/40">
          <Sparkles className="w-3.5 h-3.5 text-amber-600" />
          <span>{isHi ? `सक्रिय गोचर निरीक्षण: ${selectedTransitPlanet}` : `Active Transit Focus: ${selectedTransitPlanet}`}</span>
        </span>
        {highlightedHouses.length > 0 && (
          <span className="text-[11px] text-astro-secondary-text">
            {isHi ? 'स्थान भाव:' : 'Occupies:'} <strong className="text-astro-primary">H{highlightedHouses.join(', H')}</strong>
          </span>
        )}
        {highlightedAspectHouses.length > 0 && (
          <span className="text-[11px] text-astro-secondary-text">
            {isHi ? 'दृष्टि भाव:' : 'Drishti Rays:'} <strong className="text-purple-700 dark:text-purple-300">H{highlightedAspectHouses.join(', H')}</strong>
          </span>
        )}
        {affectedNatalPlanets.length > 0 ? (
          <span className="text-[11px] text-astro-secondary-text">
            {isHi ? 'प्रभावित जन्म ग्रह:' : 'Contacted Natal Grahas:'} <strong className="text-emerald-700 dark:text-emerald-300">{affectedNatalPlanets.join(', ')}</strong>
          </span>
        ) : (
          <span className="text-[11px] text-astro-secondary-text italic">
            {isHi ? '(कोई प्रत्यक्ष युति/दृष्टि नहीं)' : '(No direct natal graha aspect)'}
          </span>
        )}
      </div>
      {onSelectTransitPlanet && (
        <button
          onClick={() => onSelectTransitPlanet(selectedTransitPlanet === 'Saturn' ? 'Jupiter' : 'Saturn')}
          className="text-[10px] text-astro-secondary-text hover:text-astro-primary font-semibold px-2 py-0.5 rounded bg-astro-card border border-astro-border transition-colors"
        >
          {isHi ? 'ग्रह बदलें' : 'Switch Planet'}
        </button>
      )}
    </div>
  ) : null;

  const natalCard = (
    <div className="w-full bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col items-center">
      <div className="w-full flex items-center justify-between border-b border-astro-border pb-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-astro-primary" />
          <h4 className="font-bold text-astro-primary font-serif text-base sm:text-lg">
            {isHi ? 'जन्म लग्न कुंडली (D1 Natal Chart)' : 'Natal Chart (D1 Lagna Kundali)'}
          </h4>
        </div>
        <span className="text-xs text-astro-secondary-text px-2.5 py-1 bg-astro-card rounded-lg border border-astro-border font-mono font-semibold">
          {formatDOB(kundali.birthDetails.date)} {kundali.birthDetails.time}
        </span>
      </div>
      <ResponsiveChartContainer maxDimension={scaledMaxDim} minDimension={220} className="w-full">
        {() => renderSvg(false)}
      </ResponsiveChartContainer>
    </div>
  );

  const transitCard = (
    <div className="w-full bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-6 shadow-sm flex flex-col items-center">
      <div className="w-full flex flex-col sm:flex-row sm:items-center justify-between border-b border-astro-border pb-3 mb-4 gap-2">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-astro-accent animate-pulse" />
          <h4 className="font-bold text-astro-primary font-serif text-base sm:text-lg">
            {isHi ? 'गोचर कुंडली (चयनित समय)' : 'Live Gochar (Transit Chart)'}
          </h4>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-astro-primary px-2.5 py-1 bg-astro-accent/15 rounded-lg border border-astro-accent/30 font-bold font-mono">
            {transitDateFormatted}
          </span>
        </div>
      </div>

      {/* Gochar Period Section (Start & End Dates for Transit) */}
      <div className="w-full bg-astro-card border border-astro-border rounded-xl p-3 sm:p-4 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-astro-border/60 pb-2.5 mb-2.5">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-astro-primary" />
            <span className="text-xs font-bold text-astro-primary font-serif uppercase tracking-wider">
              {isHi ? 'गोचर काल अवधि (Start & End Date)' : 'Gochar Period (Start & End Date)'}
            </span>
          </div>
          
          {/* Planet Selector for Quick Period Inspection */}
          <div className="flex items-center gap-1 overflow-x-auto py-0.5 max-w-full">
            {(['Saturn', 'Jupiter', 'Rahu', 'Ketu', 'Mars', 'Sun', 'Venus', 'Mercury', 'Moon'] as PlanetName[]).map((p) => (
              <button
                key={p}
                onClick={() => {
                  setLocalPeriodPlanet(p);
                  if (onSelectTransitPlanet) onSelectTransitPlanet(p);
                }}
                className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all shrink-0 ${
                  activeFocusPlanet === p
                    ? 'bg-astro-primary text-white shadow-xs'
                    : 'bg-astro-bg text-astro-secondary-text border border-astro-border hover:text-astro-primary'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Selected Planet Gochar Period Detail Card */}
        {activePeriod && (
          <div className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs">
              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border">
                <span className="text-astro-secondary-text block text-[10px] uppercase font-semibold">
                  {isHi ? 'प्रारंभ तिथि (Ingress)' : 'Current Ingress Date'}
                </span>
                <span className="font-bold text-astro-text font-mono text-xs sm:text-sm">
                  {activePeriod.startDateFormatted}
                </span>
                <span className="text-[10px] text-astro-secondary-text block mt-0.5">
                  {activePeriod.planet} in {activePeriod.sign} ({activePeriod.degreeFormatted})
                </span>
              </div>

              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border">
                <span className="text-astro-secondary-text block text-[10px] uppercase font-semibold">
                  {isHi ? 'समाप्ति तिथि (Egress)' : 'Current Sign Egress'}
                </span>
                <span className="font-bold text-astro-primary font-mono text-xs sm:text-sm">
                  {activePeriod.endDateFormatted}
                </span>
                <span className="text-[10px] text-astro-secondary-text block mt-0.5">
                  {isHi ? `कुल अवधि: ${activePeriod.durationFormatted}` : `Duration: ${activePeriod.durationFormatted}`}
                </span>
              </div>

              <div className="p-2.5 bg-astro-card rounded-lg border border-astro-primary/30">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-astro-primary uppercase font-bold">
                    {isHi ? 'अगली राशि एवं काल अवधि (From - To)' : 'Next Period (From - To)'}
                  </span>
                  <span className="font-mono text-[9.5px] font-bold text-astro-primary bg-astro-primary/10 px-1.5 py-0.2 rounded border border-astro-primary/20">
                    {isHi ? `${activePeriod.daysUntilNextIngress} दिन शेष` : `${activePeriod.daysUntilNextIngress}d left`}
                  </span>
                </div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="font-bold text-astro-primary text-xs sm:text-sm">
                    {activePeriod.nextSign}
                  </span>
                </div>
                <div className="text-[10px] font-mono text-astro-text font-semibold mt-0.5 flex items-center gap-1">
                  <span>{activePeriod.nextPeriodStartDateFormatted}</span>
                  <span className="text-astro-secondary-text font-normal">→</span>
                  <span>{activePeriod.nextPeriodEndDateFormatted}</span>
                </div>
                <span className="text-[9.5px] text-astro-secondary-text block mt-0.5">
                  {isHi ? `आगामी अवधि: ${activePeriod.nextDurationFormatted}` : `Next duration: ${activePeriod.nextDurationFormatted}`}
                </span>
              </div>

              <div className="p-2.5 bg-astro-bg rounded-lg border border-astro-border flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between text-[10px] font-semibold text-astro-secondary-text">
                    <span>{isHi ? 'गोचर प्रगति' : 'Transit Progress'}</span>
                    <span className="font-mono text-astro-primary">{activePeriod.progressPercentage}%</span>
                  </div>
                  <div className="w-full bg-astro-border rounded-full h-1.5 mt-1 overflow-hidden">
                    <div
                      className="bg-astro-primary h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${activePeriod.progressPercentage}%` }}
                    />
                  </div>
                </div>
                <div className="text-[10px] text-astro-secondary-text mt-1 flex items-center justify-between">
                  <span>{activePeriod.daysElapsed}d {isHi ? 'बीते' : 'past'}</span>
                  <span className="font-semibold text-astro-text">{activePeriod.daysRemaining}d {isHi ? 'शेष' : 'left'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ResponsiveChartContainer maxDimension={scaledMaxDim} minDimension={220} className="w-full">
        {() => renderSvg(true)}
      </ResponsiveChartContainer>
    </div>
  );

  if (layoutMode === 'natal') {
    return (
      <div className="w-full max-w-5xl mx-auto space-y-3">
        {crossHighlightBar}
        {natalCard}
      </div>
    );
  }

  if (layoutMode === 'transit') {
    return (
      <div className="w-full max-w-5xl mx-auto space-y-3">
        {crossHighlightBar}
        {transitCard}
      </div>
    );
  }

  if (layoutMode === 'stacked') {
    return (
      <div className="w-full flex flex-col gap-4 max-w-5xl mx-auto">
        {crossHighlightBar}
        {natalCard}
        {transitCard}
      </div>
    );
  }

  // Default: Grid (Side by side on larger screens, full width per column)
  return (
    <div className="w-full space-y-3 min-w-0">
      {crossHighlightBar}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 w-full min-w-0">
        <div className="w-full min-w-0">{natalCard}</div>
        <div className="w-full min-w-0">{transitCard}</div>
      </div>
    </div>
  );
};

